import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState as useLocalState,
} from "react";
import Flatpickr from "react-flatpickr";
import classnames from "classnames";
import { Swedish } from "flatpickr/dist/l10n/sv";
import PropTypes from "prop-types";
import { useState } from "../hooks/useState";
import { isValidDate, parseStringAsDate } from "../utils";

function DateInput({
  name,
  children,
  error,
  register,
  unregister,
  pattern,
  required,
  setValue,
  setError,
  minDate,
  ...rest
}) {
  const [isSameDay, setIsSameDay] = useLocalState(false);
  const { browser } = useState();
  const inputClass = classnames("input", {
    "is-danger": !!error,
    "previa-datetime-picker-input": !browser.datetimeSupport,
  });
  const dateInputRef = useRef();
  const internalDateRef = useRef();
  const internalTimeRef = useRef();

  const isTouchDeviceWithDateTimeSupport = useMemo(
    () => browser.datetimeSupport && browser.touch,
    [browser]
  );

  useEffect(() => {
    register({ name }, { required, pattern });
    return () => unregister({ name });
  }, [dateInputRef]);

  const setDateTimeOnInput = ({ date, time }) => {
    const dateInputNode = dateInputRef.current;
    const [inputDate, inputTime] = dateInputNode.value.split(" ");

    dateInputNode.value = [date || inputDate, time || inputTime].join(" ");
    setValue(name, dateInputNode.value, { shouldValidate: true });
  };

  const min = useMemo(() => {
    if (minDate && isValidDate(minDate)) {
      // Strip extra data from datestring and add current
      // timezone lost when parsing Date to string in JS.
      const tzoffset = minDate.getTimezoneOffset() * 60000;
      const date = new Date(minDate - tzoffset).toISOString().slice(0, -8);
      return date;
    }
    return minDate;
  }, [minDate, isTouchDeviceWithDateTimeSupport]);

  const updateDateTime = useCallback(
    ({ target }) => {
      // this is for the native datetime-local
      const updatedTime = target.value.substring(0, 16);
      const [date, time] = updatedTime.split("T");

      if (parseStringAsDate(target.value) < minDate) {
        setError(name, {
          type: "invalidDate",
          message: `Minsta datum: ${min.replace("T", " - ")}`,
        });
        return setDateTimeOnInput({ date: "x", time: "x" });
      }
      return setDateTimeOnInput({ date, time });
    },
    [name, dateInputRef, minDate]
  );

  const minTime = useMemo(() => {
    if (!internalDateRef.current) return null;
    const { flatpickr: instance } = internalDateRef.current;
    return isSameDay && instance.formatDate(minDate, "H:i");
  }, [isSameDay, internalDateRef, minDate]);

  const compareMinDateWith = useCallback(
    (currentDate) =>
      !!minDate &&
      minDate.getDate() === currentDate.getDate() &&
      minDate.getMonth() === currentDate.getMonth() &&
      minDate.getYear() === currentDate.getYear(),
    [minDate]
  );

  return (
    <div className="field">
      <label htmlFor={name} className="label">
        {children}
      </label>
      <div className="field-body">
        <input type="hidden" ref={dateInputRef} id={name} name={name} />
        {isTouchDeviceWithDateTimeSupport ? (
          <div className="field">
            <div className="control">
              <input
                {...rest}
                className={inputClass}
                name={name}
                id={`${name}-internal`}
                type="datetime-local"
                onChange={updateDateTime}
                min={min}
                readOnly={browser.isIOS}
              />
            </div>
          </div>
        ) : (
          <div className="field is-grouped">
            <div className="control is-expanded">
              <Flatpickr
                {...rest}
                ref={internalDateRef}
                name={name}
                id={`${name}-internal-date`}
                onChange={(_, date) => date && setDateTimeOnInput({ date })}
                onValueUpdate={([date]) =>
                  !!date && setIsSameDay(compareMinDateWith(date))
                }
                placeholder="YYYY-MM-DD"
                className={inputClass}
                options={{
                  locale: Swedish,
                  minDate,
                }}
              />
            </div>
            <div className="control">
              <Flatpickr
                {...rest}
                ref={internalTimeRef}
                name={name}
                id={`${name}-internal-time`}
                onChange={(_, time) => setDateTimeOnInput({ time })}
                placeholder="--:--"
                className={inputClass}
                options={{
                  locale: Swedish,
                  enableTime: true,
                  noCalendar: true,
                  minuteIncrement: 1,
                  defaultHour: 13,
                  minTime,
                }}
              />
            </div>
          </div>
        )}
      </div>
      {error && <p className="help is-primary">{error.message}</p>}
    </div>
  );
}

DateInput.defaultProps = {
  error: null,
  required: false,
  minDate: null,
};

DateInput.propTypes = {
  children: PropTypes.string.isRequired,
  error: PropTypes.shape({ message: PropTypes.string }),
  name: PropTypes.string.isRequired,
  pattern: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.shape({ value: PropTypes.object, message: PropTypes.string }),
  ]),
  unregister: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  required: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ value: PropTypes.bool, message: PropTypes.string }),
  ]),
  setValue: PropTypes.func.isRequired,
  setError: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
};
export default DateInput;
